import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, getToken } from "@/lib/api";
import { SKILLS } from "@/lib/skills";
import { ArabicText } from "@/components/ArabicText";
import { Download, DoorOpen } from "lucide-react";
import { t } from "@/i18n";

const SECTIONS = ["reading", "grammar", "writing"];
const LABELS = { reading: "Compréhension écrite", grammar: "Grammaire & vocabulaire", writing: "Production écrite" };

export default function Exam() {
  const { level } = useParams();
  const [exam, setExam] = useState(null);
  const [step, setStep] = useState(-1); // -1 intro
  const [ans, setAns] = useState({ reading: [], grammar: [], writing: "" });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/exam/${level}`).then((r) => setExam(r.data)).catch((e) => setErr(e.response?.data?.detail || "Indisponible"));
  }, [level]);

  const setMcq = (sec, i, v) => setAns((a) => { const arr = [...a[sec]]; arr[i] = v; return { ...a, [sec]: arr }; });

  const submit = async () => {
    const { data } = await api.post("/exam/submit", { level, ...ans });
    setResult(data);
    setStep(99);
  };

  const dl = async () => {
    const res = await fetch(`${API}/exam/attestation/${result.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `attestation_${level}.pdf`; a.click();
  };

  if (err) return <div className="max-w-lg mx-auto rounded-2xl border bg-card p-8 text-center text-muted-foreground">{err}</div>;
  if (!exam) return <div className="text-muted-foreground">{t("lessoneditor.chargement")}</div>;

  const Mcq = ({ sec, qs }) => (
    <div className="space-y-4">
      {qs.map((q, i) => (
        <div key={i} className="rounded-2xl border bg-card p-4" data-testid={`${sec}-q-${i}`}>
          <p className="font-medium mb-3">{q.prompt}</p>
          <div className="space-y-2">
            {q.options.map((o, oi) => (
              <button key={oi} data-testid={`${sec}-q-${i}-opt-${oi}`} onClick={() => setMcq(sec, i, oi)}
                className={`w-full text-left px-4 py-2.5 rounded-xl border transition ${ans[sec][i] === oi ? "border-primary bg-primary/10" : "hover:border-primary/50"}`}>{o}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      {step === -1 && (
        <div className="rounded-2xl border bg-card p-8 text-center" data-testid="exam-intro">
          <DoorOpen size={40} className="mx-auto mb-3" style={{ color: SKILLS.exam.color }} />
          <h1 className="font-head text-2xl font-bold mb-2">{t("exam.examen_blanc")} {level}</h1>
          <p className="text-muted-foreground mb-6">{t("exam.format_cecrl_en_3_parties_comprehension")} {exam.duration_min} {t("exam.min_un_score_global_de_60")}</p>
          <button data-testid="start-exam" onClick={() => setStep(0)} className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold">{t("exam.commencer_l_examen")}</button>
        </div>
      )}

      {step >= 0 && step < 3 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            {SECTIONS.map((sec, i) => <div key={sec} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-secondary"}`} />)}
          </div>
          <h2 className="font-head text-xl font-bold mb-4">{t("exam.partie")} {step + 1} — {LABELS[SECTIONS[step]]}</h2>

          {step === 0 && (<>
            <div className="rounded-2xl border bg-card p-4 mb-4 space-y-2">
              {exam.reading.text.map((t, i) => <ArabicText key={i} size="text-2xl">{t.ar}</ArabicText>)}
            </div>
            <Mcq sec="reading" qs={exam.reading.questions} />
          </>)}
          {step === 1 && <Mcq sec="grammar" qs={exam.grammar.questions} />}
          {step === 2 && (
            <div className="rounded-2xl border bg-card p-4">
              <p className="mb-3">{exam.writing.prompt}</p>
              <textarea data-testid="writing-input" dir="rtl" rows={5} value={ans.writing}
                onChange={(e) => setAns((a) => ({ ...a, writing: e.target.value }))}
                className="w-full arabic text-xl px-4 py-3 rounded-xl border bg-card outline-none focus:ring-2 ring-primary" />
              <div className="text-xs text-muted-foreground mt-1">{t("exam.minimum")} {exam.writing.min_words} {t("exam.mots")}</div>
            </div>
          )}

          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(step - 1)} disabled={step === 0} className="px-5 py-2.5 rounded-xl border font-medium disabled:opacity-40">{t("tutorial.precedent")}</button>
            {step < 2 ? (
              <button data-testid="exam-next" onClick={() => setStep(step + 1)} className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium">{t("dictionary.suivant")}</button>
            ) : (
              <button data-testid="exam-submit" onClick={submit} className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium">{t("exam.terminer_l_examen")}</button>
            )}
          </div>
        </div>
      )}

      {step === 99 && result && (
        <div className="rounded-2xl border bg-card p-6" data-testid="exam-report">
          <h2 className="font-head text-2xl font-bold mb-1">{t("exam.resultat")} {result.overall}%</h2>
          <p className={`font-medium mb-5 ${result.passed ? "text-skill-vocabulary" : "text-destructive"}`} style={result.passed ? { color: "#16A34A" } : {}}>
            {result.passed ? `Niveau ${level} validé !` : t("exam.niveau_non_valide_revois_les_points")}
          </p>
          <div className="space-y-3 mb-6">
            {Object.entries(result.scores).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1"><span>{LABELS[k]}</span><span>{v}%</span></div>
                <div className="h-2 bg-secondary rounded-full"><div className="h-full rounded-full" style={{ width: `${v}%`, background: v >= 60 ? "#16A34A" : "#DC2626" }} /></div>
              </div>
            ))}
          </div>
          <div className="flex gap-3 flex-wrap">
            {result.passed && <button data-testid="dl-attestation" onClick={dl} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-ocre text-white font-medium"><Download size={16} /> {t("exam.telecharger_l_attestation")}</button>}
            <button onClick={() => nav("/progress")} className="px-5 py-2.5 rounded-xl border font-medium">{t("exam.voir_ma_progression")}</button>
            <button onClick={() => nav("/dashboard")} className="px-5 py-2.5 rounded-xl border font-medium">{t("exam.dashboard")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
